import { ExtractComponentProp } from '@approvalmax/types';
import { Button, Flex, Form, Grid, Link, Menu, Popup, Select, Text } from '@approvalmax/ui/src/components';
import { useAtom, useSetAtom } from 'jotai';
import { backend } from 'modules/data';
import { memo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { BankLogoImage } from 'shared/components';
import { companyIdQueryParam } from 'shared/constants';
import { useCreateAmaxPayConsent } from 'shared/data/webApp/v1';
import { getCountryFlagByCountryCode } from 'shared/helpers/getCountryFlagByCountryCode';
import { getUrl, Path } from 'urlBuilder';

import { bankingAppLoginPopupState } from '../../../BankingAppLoginPopup/BankingAppLoginPopup.states';
import { connectBankAccountPopupOpenState } from '../../ConnectBankAccountPopup.states';
import { useBanks } from './ConnectBankAccountPopupContent.hooks';
import { messages } from './ConnectBankAccountPopupContent.messages';
import type { FormState } from './ConnectBankAccountPopupContent.types';

export const ConnectBankAccountPopupContent = memo(() => {
    const { companyId } = useParams<{ companyId: string }>();
    const [open, toggleOpen] = useAtom(connectBankAccountPopupOpenState);
    const form = useForm<FormState>({
        defaultValues: {
            // since we have only one country now, it doesn't make too much
            // sense to change this field
            countryCode: backend.CountryCode.GB,
        },
    });

    const setBankingAppLoginPopupState = useSetAtom(bankingAppLoginPopupState);

    const countryCode = form.watch('countryCode');
    const { banks, countries, isLoading } = useBanks(companyId, open, countryCode);

    const { mutate: createAmaxPayConsent, isLoading: isLoadingCreateAmaxPayConsent } = useCreateAmaxPayConsent({
        onSuccess: ({ data }, variables) => {
            toggleOpen(false);
            setBankingAppLoginPopupState({
                consentId: data.id,
                bankId: variables.body?.bankId,
                authorisationUrl: data.authorisationUrl,
                createdAt: new Date(data.createdAt),
            });
        },
    });

    const customBankMenuItem = useCallback<
        ExtractComponentProp<typeof Select<(typeof banks)[number]>, 'customMenuItem'>
    >(({ key, ...item }) => {
        return (
            <Menu.Item
                key={key}
                {...item}
                startIcon={<BankLogoImage src={item.iconUrl} alt={item.name} width={16} height={16} />}
            />
        );
    }, []);

    const customCountryMenuItem = useCallback<
        ExtractComponentProp<typeof Select<(typeof countries)[number]>, 'customMenuItem'>
    >((item) => {
        const Icon = getCountryFlagByCountryCode(item.id);

        return <Menu.Item {...item} startIcon={Icon ? <Icon height={16} /> : undefined} />;
    }, []);

    const handleSubmit = form.handleSubmit((data) => {
        void createAmaxPayConsent({
            body: {
                callbackUrl: `${getUrl(Path.connectBankAccountSuccess)}?${companyIdQueryParam}=${companyId}`,
                bankId: data.bankId,
            },
            params: { path: { companyId } },
        });
    });

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <Popup.Header
                title={messages.title}
                actions={
                    <Button
                        disabled={isLoading || isLoadingCreateAmaxPayConsent}
                        type='submit'
                        color='blue80'
                        size='medium'
                    >
                        {messages.next}
                    </Button>
                }
                progress={isLoading || isLoadingCreateAmaxPayConsent}
            />

            <Popup.Body spacing='32'>
                <Flex direction='column' spacing='24'>
                    <Flex direction='column' spacing='4'>
                        <Text font='body' fontSize='large'>
                            {messages.bankingAppMustBeConnected({
                                learnMore: (
                                    <Link
                                        external
                                        font='body'
                                        fontSize='large'
                                        href='https://support.approvalmax.com/portal/en/kb/articles/how-to-setup-a-bank-account-connection-in-approvalmax'
                                    >
                                        {messages.learnMore}
                                    </Link>
                                ),
                            })}
                        </Text>
                    </Flex>

                    <Grid gridTemplateColumns='180px auto' gap={12} alignItems='center'>
                        <Select
                            autocomplete
                            required
                            // since we have only one country now, it doesn't make too much
                            // sense to interact w/ this select
                            disabled
                            name='countryCode'
                            placeholder={messages.searchCountry}
                            items={countries}
                            customMenuItem={customCountryMenuItem}
                        />

                        <Select
                            autocomplete
                            required
                            name='bankId'
                            placeholder={messages.searchBank}
                            items={banks}
                            customMenuItem={customBankMenuItem}
                        />
                    </Grid>

                    <Flex direction='column' spacing='4' alignItems='center'>
                        <Text font='label' fontSize='large' color='midnight70'>
                            {messages.notTheBankAccountHolder}
                        </Text>

                        <Text font='label' fontSize='large'>
                            {messages.pleaseContactAdmin}
                        </Text>
                    </Flex>
                </Flex>
            </Popup.Body>
        </Form>
    );
});

ConnectBankAccountPopupContent.displayName = 'ConnectBankAccountPopupContent';
